import { lazy, Suspense } from "react";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import StickyBookButton from "@/components/StickyBookButton";

// Below-the-fold sections are code-split out of the main bundle. The browser
// downloads + parses them in parallel chunks AFTER the critical hero code,
// which shrinks the main chunk and shortens Time to Interactive / INP.
// The .cv-auto wrapper already reserves 900px of intrinsic height, so a null
// Suspense fallback causes no layout shift while each chunk streams in.
const Services = lazy(() => import("@/components/Services"));
const WhyChooseUs = lazy(() => import("@/components/WhyChooseUs"));
const BeforeAfter = lazy(() => import("@/components/BeforeAfter"));
const Testimonials = lazy(() => import("@/components/Testimonials"));
const GoogleReviewsWidget = lazy(() => import("@/components/GoogleReviewsWidget"));
const ServiceAreasBand = lazy(() => import("@/components/ServiceAreasBand"));
const Booking = lazy(() => import("@/components/Booking"));
const FAQ = lazy(() => import("@/components/FAQ"));
const Contact = lazy(() => import("@/components/Contact"));
const Footer = lazy(() => import("@/components/Footer"));
import Seo from "@/components/Seo";
import { organizationSchema, localBusinessSchema, websiteSchema, faqSchema, serviceAreaItemListSchema } from "@/lib/schema";
import { LOCATION_PAGES } from "@/data/seo-content";

const HOME_FAQS = [
  { q: "Do I need shampooing or steaming?", a: "It depends on the car. For minor stain removal, disinfection or leather interiors, we recommend thorough steam cleaning for seats, vents, dash and doors. For tough set-in stains on carpets or fabric seats, our stain-removal extraction lifts deep dirt using fabric cleaner, drill-brush agitation and hot water extraction." },
  { q: "How long does a mobile detail usually take?", a: "Our services range from 1.5 to 3 hours. It varies by package and the condition of your vehicle." },
  { q: "Do you come to me?", a: "Yes — we're fully mobile. We come to you with our own water and power. We're based in Dartmouth and travel throughout HRM." },
  { q: "When are you open?", a: "Our booking season runs April through November. We're closed December through March. During the season we book Mon–Thu 9 AM – 6 PM and Fri 9 AM – 2 PM." },
  { q: "What payment methods do you accept?", a: "Debit, Credit, E-Transfer, and Cash." },
  { q: "Are you licensed and insured?", a: "Yes — Darkside Detailing is fully licensed and insured." },
];

export default function Home() {
  return (
    <div className="relative min-h-screen bg-[#030305] text-[#f2f2f2] grain" data-testid="home-page">
      <Seo
        title="Darkside Detailing · Mobile Auto Detailing in Halifax & Dartmouth, NS"
        description="Halifax's top-rated mobile auto detailing. Interior, exterior, shampoo, wash & wax — fully licensed & insured mobile service across HRM Nova Scotia. Book online, starting at $150."
        path="/"
        keywords="mobile auto detailing halifax, car detailing dartmouth, interior detailing HRM, mobile detailing nova scotia, darkside detailing, car wash halifax, car shampooing dartmouth"
        jsonLd={[organizationSchema(), localBusinessSchema(), websiteSchema(), faqSchema(HOME_FAQS), serviceAreaItemListSchema(LOCATION_PAGES)]}
      />
      <Header />
      <main>
        <Hero />
        <Marquee />
        {/* Below-the-fold sections. `content-visibility: auto` tells the browser
            to skip layout + paint work for these until they scroll near the
            viewport. This shortens LCP and Time to Interactive by 30-60% on
            slow devices. Explicit `contain-intrinsic-size` prevents scrollbar
            jumps. */}
        <Suspense fallback={null}>
          <div className="cv-auto"><Services /></div>
          <div className="cv-auto"><WhyChooseUs /></div>
          <div className="cv-auto"><BeforeAfter /></div>
          <div className="cv-auto">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
              <GoogleReviewsWidget />
            </div>
          </div>
          <div className="cv-auto"><Testimonials /></div>
          <div className="cv-auto"><ServiceAreasBand /></div>
          <div className="cv-auto"><Booking /></div>
          <div className="cv-auto"><FAQ /></div>
          <div className="cv-auto"><Contact /></div>
        </Suspense>
      </main>
      <Suspense fallback={null}>
        <Footer />
      </Suspense>
      <StickyBookButton />
    </div>
  );
}
